import { useState } from "react";
import type { Exercise, WritingAttemptResult } from "../../types";
import { api } from "../../api/client";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

export default function WritingExercise({ exercise, onDone, onSkip }: Props) {
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<WritingAttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    try {
      setResult(await api.attemptWriting(exercise.id, answer));
    } catch (err) {
      console.error("Writing grading failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => setResult(null);

  return (
    <div>
      {exercise.explanation && (
        <p className="text-[0.8125rem] text-text-tertiary italic mb-4">{exercise.explanation}</p>
      )}

      {!result && (
        <>
          <textarea
            className="input-base w-full resize-none focus:outline-none"
            rows={5}
            value={answer}
            autoFocus
            placeholder="Scrivi qui in italiano…"
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          />
          <div className="flex items-center gap-2 mt-3">
            <button className="btn-primary text-sm px-5" onClick={submit} disabled={submitting || !answer.trim()}>
              {submitting ? "Grading…" : "Submit"} <span className="opacity-60 ml-1">⌘↵</span>
            </button>
            {onSkip && <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>}
          </div>
        </>
      )}

      {result && (
        <FeedbackPanel
          correct={result.passed}
          title={`${result.passed ? "Passed" : "Keep practicing"} · ${Math.round(result.score)}/100`}
          onNext={() => onDone(result.passed)}
          onRetry={retry}
        >
          {result.feedback && <div className="italic">{result.feedback}</div>}
          {result.corrections.length > 0 && (
            <div className="pt-1 space-y-1">
              {result.corrections.map((c, i) => (
                <div key={i} className="text-[0.8125rem]">
                  <span className="text-error line-through decoration-error/40">{c.original}</span>
                  {" → "}
                  <span className="text-success font-medium">{c.corrected}</span>
                </div>
              ))}
            </div>
          )}
        </FeedbackPanel>
      )}
    </div>
  );
}
